import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TagsInput } from '@/components/ui/tags-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useCreateSynonym, useUpdateSynonym } from 'shared/hooks/search-synonyms.hooks';

const MAPPING_TYPES = [
  { value: 'equivalent', label: 'Equivalent (bidirectional)' },
  { value: 'explicit', label: 'Explicit (one-directional)' },
];

const trimmedTerms = z.array(z.string().trim().min(1, 'Terms cannot be empty'));

// Mirrors the backend Joi rules (SEARCH_SYNONYMS.md §5): synonyms always ≥1;
// input required ≥1 for explicit, must be empty for equivalent.
const SYNONYM_SCHEMA = z
  .object({
    mappingType: z.enum(['equivalent', 'explicit']),
    synonyms: trimmedTerms.min(1, 'Add at least one synonym'),
    input: trimmedTerms,
    note: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.mappingType === 'explicit' && data.input.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['input'],
        message: 'Explicit mappings need at least one input term',
      });
    }
  });

const emptyValues = { mappingType: 'equivalent', synonyms: [], input: [], note: '' };

const toFormValues = record =>
  record
    ? {
        mappingType: record.mappingType,
        synonyms: record.synonyms ?? [],
        input: record.input ?? [],
        note: record.note ?? '',
      }
    : emptyValues;

/**
 * Create/edit dialog for a synonym mapping. Editing when `record` is set,
 * creating otherwise. The `mappingType` toggle drives whether the `input` field
 * is shown (equivalent hides it and forces an empty array).
 */
export function SynonymFormDialog({ open, record, onOpenChange, onSuccess }) {
  const { toast } = useToast();
  const createSynonym = useCreateSynonym();
  const updateSynonym = useUpdateSynonym();
  const isEdit = !!record;

  const form = useForm({
    resolver: zodResolver(SYNONYM_SCHEMA),
    defaultValues: emptyValues,
  });

  // Reset the form whenever the dialog opens (with the record's values on edit).
  useEffect(() => {
    if (open) form.reset(toFormValues(record));
  }, [open, record, form]);

  const mappingType = form.watch('mappingType');
  const isExplicit = mappingType === 'explicit';

  const onSubmit = async values => {
    const payload = {
      mappingType: values.mappingType,
      synonyms: values.synonyms,
      input: values.mappingType === 'explicit' ? values.input : [],
      note: values.note?.trim() || '',
    };

    try {
      if (isEdit) {
        await updateSynonym.mutateAsync({ id: record.id, payload });
        toast({ title: 'Mapping updated' });
      } else {
        await createSynonym.mutateAsync(payload);
        toast({ title: 'Mapping created' });
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: isEdit ? 'Failed to update mapping' : 'Failed to create mapping',
        description: err?.response?.data?.message || err?.message || 'Unexpected error',
        variant: 'destructive',
      });
    }
  };

  const submitting = createSynonym.isPending || updateSynonym.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit synonym mapping' : 'New synonym mapping'}</DialogTitle>
          <DialogDescription>
            Synonym mappings are global and apply across all organizations.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="mappingType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Mapping type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MAPPING_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {isExplicit ? (
                      <>
                        One-way: a query matching an input term also searches the synonyms, but not
                        the reverse. E.g. input “doctor” → also finds “physician”, “clinician”.
                      </>
                    ) : (
                      <>
                        Bidirectional: all terms are interchangeable. E.g. “heart attack” ↔ “mi” ↔
                        “myocardial infarction” — searching any finds the rest.
                      </>
                    )}
                  </FormDescription>
                </FormItem>
              )}
            />

            {isExplicit && (
              <FormField
                control={form.control}
                name="input"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Input (trigger terms)</FormLabel>
                    <FormControl>
                      <TagsInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="e.g. doctor"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="synonyms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Synonyms</FormLabel>
                  <FormControl>
                    <TagsInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={
                        isExplicit ? 'e.g. physician, clinician' : 'e.g. mi, heart attack'
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Note</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional label, e.g. cardiology" {...field} />
                  </FormControl>
                  <FormDescription>For admins only — not used by search.</FormDescription>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                {isEdit ? 'Save changes' : 'Create mapping'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
